import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Upload, Trash2, Download, Image, Video, Play, ChevronLeft, ChevronRight, ZoomIn, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { jobsAPI } from '../utils/api';

const GalleryModal = ({ isOpen, onClose, jobId, jobCode, canEdit }) => {
  const [gallery, setGallery] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && jobId) {
      loadGallery();
    }
  }, [isOpen, jobId, searchTerm]);

  const loadGallery = async () => {
    try {
      setLoading(true);
      const res = await jobsAPI.getGallery(jobId, searchTerm);
      setGallery(res.data.gallery || []);
      setGrouped(res.data.grouped || {});
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    // Filter to only images and videos
    const mediaFiles = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (mediaFiles.length !== files.length) {
      toast.warning('Some files were skipped (only images and videos allowed)');
    }
    setSelectedFiles(mediaFiles);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    try {
      await jobsAPI.uploadGallery(jobId, selectedFiles, uploadDescription);
      toast.success(`${selectedFiles.length} file(s) uploaded successfully`);
      setSelectedFiles([]);
      setUploadDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadGallery();
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this item from the gallery?')) return;
    
    try {
      await jobsAPI.deleteGalleryItem(jobId, itemId);
      toast.success('Item deleted');
      loadGallery();
      if (lightboxItem?.id === itemId) {
        setLightboxItem(null);
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleDownload = (item) => {
    try {
      const byteCharacters = atob(item.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: item.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const openLightbox = (item, index) => {
    setLightboxItem(item);
    setLightboxIndex(index);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < gallery.length) {
      setLightboxIndex(newIndex);
      setLightboxItem(gallery[newIndex]);
    }
  };

  const getMediaSrc = (item) => {
    return `data:${item.content_type};base64,${item.data}`;
  };

  const isVideo = (item) => item.content_type?.startsWith('video/');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Job Gallery</h2>
            <p className="text-sm text-slate-500">{jobCode} - {gallery.length} item(s)</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Search and Upload */}
        <div className="p-4 border-b border-slate-200 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by description..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Upload Section */}
          {canEdit && (
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Select Photos/Videos
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileSelect}
                    className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description (groups files)
                  </label>
                  <input
                    type="text"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="e.g., Before photos, Progress Day 1..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload {selectedFiles.length > 0 && `(${selectedFiles.length})`}
                </button>
              </div>
              {selectedFiles.length > 0 && (
                <div className="mt-2 text-sm text-slate-600">
                  Selected: {selectedFiles.map(f => f.name).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Gallery Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : gallery.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <Image className="w-12 h-12 mb-2 text-slate-300" />
              <p>No photos or videos yet</p>
              {canEdit && <p className="text-sm">Upload some to get started</p>}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(grouped).map(([description, items]) => (
                <div key={description} className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">
                    {description || 'Ungrouped'} ({items.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {items.map((item, idx) => (
                      <div
                        key={item.id}
                        className="relative group aspect-square bg-slate-100 rounded-lg overflow-hidden border border-slate-200"
                      >
                        {isVideo(item) ? (
                          <div className="w-full h-full flex items-center justify-center bg-slate-900">
                            <video
                              src={getMediaSrc(item)}
                              className="max-w-full max-h-full object-contain"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-12 h-12 bg-white bg-opacity-80 rounded-full flex items-center justify-center">
                                <Play className="w-6 h-6 text-slate-900 ml-1" />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <img
                            src={getMediaSrc(item)}
                            alt={item.filename}
                            className="w-full h-full object-cover"
                          />
                        )}
                        
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openLightbox(item, gallery.indexOf(item))}
                              className="p-2 bg-white rounded-full hover:bg-slate-100 transition-colors"
                              title="View"
                            >
                              <ZoomIn className="w-4 h-4 text-slate-700" />
                            </button>
                            <button
                              onClick={() => handleDownload(item)}
                              className="p-2 bg-white rounded-full hover:bg-slate-100 transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4 text-slate-700" />
                            </button>
                            {canEdit && (
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="p-2 bg-white rounded-full hover:bg-red-100 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Video indicator */}
                        {isVideo(item) && (
                          <div className="absolute top-2 left-2">
                            <Video className="w-4 h-4 text-white drop-shadow" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxItem && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={() => setLightboxItem(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxItem(null); }}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation */}
          {lightboxIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              className="absolute left-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {lightboxIndex < gallery.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              className="absolute right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          {/* Content */}
          <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {isVideo(lightboxItem) ? (
              <video
                src={getMediaSrc(lightboxItem)}
                controls
                autoPlay
                className="max-w-full max-h-[80vh]"
              />
            ) : (
              <img
                src={getMediaSrc(lightboxItem)}
                alt={lightboxItem.filename}
                className="max-w-full max-h-[80vh] object-contain"
              />
            )}
            <div className="text-center mt-4 text-white">
              <p className="font-medium">{lightboxItem.filename}</p>
              {lightboxItem.description && (
                <p className="text-sm text-slate-300">{lightboxItem.description}</p>
              )}
              <p className="text-xs text-slate-400 mt-1">
                {lightboxIndex + 1} of {gallery.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryModal;
